import { useState } from "react";
import { formatDistanceToNow, format } from "date-fns";
import { Download, Trash2, MoreHorizontal, Loader2, Copy } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/modern/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { cn } from "@/modern/utils";
import type { BoardContext } from "@/bryntum-lib/models/BoardContext";
import { contextDisplayName } from "../utils/contextDisplayName";
import { DeleteContextDialog } from "./DeleteContextDialog";
import { useContextViewStep, type ContextViewStep } from "../context/useContextViewState";
import { useContextPage } from "../context/ContextPageContext";

interface ContextCardProps {
  context: BoardContext;
  contexts: BoardContext[];
  isSelected: boolean;
  isExporting?: boolean;
  hasOpenAIKey: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onExport: () => void;
  onDuplicate: () => void;
  isDuplicating?: boolean;
}

type ContextState = "active" | "previously-active" | "ready" | "in-progress" | "analysing" | "questions" | "building" | "suggesting";

/** Most recently deactivated context (latest activatedAt among inactive records). */
export const findLastDeactivatedContext = (
  contexts: BoardContext[]
): BoardContext | null => {
  let latest: BoardContext | null = null;
  let latestTime = -Infinity;

  for (const ctx of contexts) {
    if (ctx.isActive || ctx.activatedAt == null) continue;
    const time = new Date(ctx.activatedAt).getTime();
    if (Number.isNaN(time)) continue;
    if (time > latestTime) {
      latestTime = time;
      latest = ctx;
    }
  }

  return latest;
};

function viewStepToState(step: ContextViewStep | null, ctx: BoardContext): ContextState {
  if (ctx.isActive) return "active";
  if (step === "context-view") return "ready";
  if (step === "analysing") return "analysing";
  if (step === "building") return "building";
  if (step === "questions") return "questions";
  if (step === "suggesting") return "suggesting";
  return "in-progress";
}

/** Returns all applicable badges for a context (supports simultaneous display). */
export const resolveContextBadges = (
  ctx: BoardContext,
  contexts: BoardContext[],
  viewStep: ContextViewStep | null
): ContextState[] => {
  const badges: ContextState[] = [];

  if (ctx.isActive) {
    badges.push("active");
    return badges;
  }

  const lastDeactivated = findLastDeactivatedContext(contexts);
  const isPreviouslyActive =
    lastDeactivated != null &&
    ctx.id === lastDeactivated.id &&
    ctx.activatedAt != null;

  const workState = viewStepToState(viewStep, ctx);

  if (workState !== "in-progress" && workState !== "active") {
    badges.push(workState);
  }

  if (isPreviouslyActive) {
    badges.push("previously-active");
  }

  if (badges.length === 0) {
    badges.push("in-progress");
  }

  return badges;
};

const STATE_META: Record<ContextState, { label: string; badgeClass: string }> = {
  active: {
    label: "Active",
    badgeClass: "bg-primary/10 text-primary",
  },
  "previously-active": {
    label: "Previously Active",
    badgeClass: "bg-muted text-muted-foreground",
  },
  ready: {
    label: "Ready",
    badgeClass: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  },
  "in-progress": {
    label: "In Progress",
    badgeClass: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  },
  analysing: {
    label: "Generating Questions…",
    badgeClass: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
  },
  questions: {
    label: "Submit Questions",
    badgeClass: "bg-orange-500/10 text-orange-600 dark:text-orange-400",
  },
  building: {
    label: "Building Context…",
    badgeClass: "bg-violet-500/10 text-violet-600 dark:text-violet-400",
  },
  suggesting: {
    label: "Generating Suggestions…",
    badgeClass: "bg-purple-500/10 text-purple-600 dark:text-purple-400",
  },
};

const formatRelative = (date: Date | null | undefined): string => {
  if (!date) return "";
  try { return formatDistanceToNow(new Date(date), { addSuffix: true }); } catch { return ""; }
};

const formatFull = (date: Date | null | undefined): string => {
  if (!date) return "";
  try { return format(new Date(date), "MMM d, yyyy 'at' h:mm a"); } catch { return ""; }
};

const formatActivatedBadge = (date: Date | null | undefined): string => {
  if (!date) return "Previously Active";
  try { return `Previously Active · ${format(new Date(date), "MMM d, yy")}`; } catch { return "Previously Active"; }
};

export const ContextCard = ({
  context,
  contexts,
  isSelected,
  isExporting = false,
  hasOpenAIKey,
  onSelect,
  onDelete,
  onExport,
  onDuplicate,
  isDuplicating = false,
}: ContextCardProps) => {
  const { canModify } = useContextPage();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const viewStep = useContextViewStep(context.id);
  const badges = resolveContextBadges(context, contexts, viewStep);
  const isLocked = context.isActive === true;
  const isCompleted = context.status === "completed";
  const showExportOrDuplicate = isCompleted;
  const showDelete = !isLocked;
  const hasMenuActions = showExportOrDuplicate || showDelete;
  const displayName = contextDisplayName(context.name);

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={(e) => {
          if (e.detail > 1) return;
          onSelect();
        }}
        onKeyDown={(e) => e.key === "Enter" && onSelect()}
        className={cn(
          "group relative flex items-center gap-3 rounded-lg cursor-pointer",
          "transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          isSelected
            ? "bg-primary/[0.08] pl-3.5 pr-3 py-2.5 border-l-2 border-l-primary"
            : "border border-transparent hover:border-border/60 hover:bg-muted/30 px-3 py-2.5"
        )}
      >
        <div className="flex-1 min-w-0">
          <p
            className={cn(
              "text-sm font-medium truncate leading-5 min-w-0",
              isSelected ? "text-foreground" : "text-foreground/90"
            )}
            title={displayName}
          >
            {displayName}
          </p>
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            {badges.map((badge) => {
              const meta = STATE_META[badge];
              const badgeLabel =
                badge === "previously-active"
                  ? formatActivatedBadge(context.activatedAt)
                  : meta.label;

              return (
                <Tooltip key={badge}>
                  <TooltipTrigger asChild>
                    <span
                      className={cn(
                        "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium leading-4",
                        meta.badgeClass
                      )}
                    >
                      {badgeLabel}
                    </span>
                  </TooltipTrigger>
                  {badge === "previously-active" && context.activatedAt && (
                    <TooltipContent side="bottom" className="text-xs">
                      Active until {formatFull(context.activatedAt)}
                    </TooltipContent>
                  )}
                </Tooltip>
              );
            })}
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="text-[11px] text-muted-foreground truncate leading-4 mt-1.5">
                {formatRelative(context.updatedAt)}
              </p>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              {formatFull(context.updatedAt)}
            </TooltipContent>
          </Tooltip>
        </div>

        {hasMenuActions && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <button
                aria-label="Context options"
                className={cn(
                  "flex h-6 w-6 shrink-0 items-center justify-center rounded-md",
                  "text-muted-foreground hover:text-foreground hover:bg-muted",
                  "opacity-0 group-hover:opacity-100 focus:opacity-100 data-[state=open]:opacity-100",
                  "transition-opacity duration-100",
                  "disabled:opacity-30 disabled:cursor-not-allowed disabled:pointer-events-none"
                )}
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              {showExportOrDuplicate && (
                <>
                  <DropdownMenuItem
                    onClick={(e) => { e.stopPropagation(); onExport(); }}
                    disabled={isExporting || !hasOpenAIKey}
                  >
                    {isExporting
                      ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                      : <Download className="h-3.5 w-3.5 mr-2" />}
                    {isExporting ? "Exporting…" : "Export"}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
                    disabled={!canModify || isDuplicating}
                  >
                    {isDuplicating
                      ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                      : <Copy className="h-3.5 w-3.5 mr-2" />}
                    {isDuplicating ? "Duplicating…" : "Duplicate"}
                  </DropdownMenuItem>
                </>
              )}
              {showDelete && (
                <>
                  {showExportOrDuplicate && <DropdownMenuSeparator />}
                  <DropdownMenuItem
                    onClick={(e) => { e.stopPropagation(); setDeleteOpen(true); }}
                    disabled={!hasOpenAIKey || !canModify}
                    className="text-destructive focus:text-destructive focus:bg-destructive/10"
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <DeleteContextDialog
        open={deleteOpen}
        contextName={context.name}
        onConfirm={() => { setDeleteOpen(false); onDelete(); }}
        onCancel={() => setDeleteOpen(false)}
      />
    </>
  );
};
